import React from "react";
import { useParams } from "react-router-dom";
import { claimRemoteSession, reconnectRemoteSession, remoteSocketUrl } from "./remoteApi";

const CONTROLLER_STORAGE_KEY = "pocket-play:website-remote-controller";
const claimRequests = new Map();

function claimOnce(pairToken) {
  if (!claimRequests.has(pairToken)) claimRequests.set(pairToken, claimRemoteSession(pairToken));
  return claimRequests.get(pairToken);
}

function storedController() {
  try { return JSON.parse(window.localStorage.getItem(CONTROLLER_STORAGE_KEY) || "null"); } catch { return null; }
}

function newMessageId() {
  return window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function Control({ control, sendAction, disabled }) {
  const [draft, setDraft] = React.useState(control.value ?? "");
  React.useEffect(() => setDraft(control.value ?? ""), [control.value]);
  const isDisabled = disabled || control.disabled;

  if (control.type === "button") {
    return <button type="button" disabled={isDisabled} onClick={() => sendAction(control.id, {})} className="min-h-14 w-full rounded-2xl border border-white/10 bg-white/[0.07] px-4 py-3 text-sm font-black text-white shadow-lg transition active:scale-[0.98] active:bg-fuchsia-500/25 disabled:opacity-35">{control.label}</button>;
  }
  if (control.type === "toggle") {
    return <button type="button" disabled={isDisabled} aria-pressed={Boolean(control.value)} onClick={() => sendAction(control.id, { value: !control.value })} className={`flex min-h-14 w-full items-center justify-between rounded-2xl border px-4 py-3 text-left text-sm font-bold ${control.value ? "border-fuchsia-300/40 bg-fuchsia-500/20 text-white" : "border-white/10 bg-white/[0.05] text-indigo-100"}`}><span>{control.label}</span><span className={`h-6 w-11 rounded-full p-1 ${control.value ? "bg-fuchsia-400" : "bg-slate-700"}`}><span className={`block h-4 w-4 rounded-full bg-white transition ${control.value ? "translate-x-5" : ""}`} /></span></button>;
  }
  if (control.type === "select") {
    return <label className="flex min-h-14 w-full flex-col gap-2 rounded-2xl border border-white/10 bg-white/[0.05] p-3 text-xs font-bold uppercase tracking-wider text-indigo-200"><span>{control.label}</span><select disabled={isDisabled} value={control.value ?? ""} onChange={(event) => sendAction(control.id, { value: event.target.value })} className="h-11 rounded-xl bg-slate-950 px-3 text-sm font-semibold normal-case tracking-normal text-white"><option value="">Default</option>{(control.options || []).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>;
  }
  if (control.type === "range") {
    return <label className="flex min-h-14 w-full flex-col gap-2 rounded-2xl border border-white/10 bg-white/[0.05] p-3 text-xs font-bold uppercase tracking-wider text-indigo-200"><span className="flex justify-between"><span>{control.label}</span><strong className="text-white">{draft}{control.unit || ""}</strong></span><input type="range" disabled={isDisabled} min={control.min} max={control.max} step={control.step || 1} value={draft} onChange={(event) => setDraft(event.target.value)} onPointerUp={() => sendAction(control.id, { value: Number(draft) })} onKeyUp={() => sendAction(control.id, { value: Number(draft) })} /></label>;
  }
  if (control.type === "text" || control.type === "number") {
    return <form className="flex w-full flex-col gap-2 rounded-2xl border border-white/10 bg-white/[0.05] p-3" onSubmit={(event) => { event.preventDefault(); sendAction(control.id, { value: control.type === "number" ? Number(draft) : String(draft) }); }}><label className="text-xs font-bold uppercase tracking-wider text-indigo-200">{control.label}</label><div className="flex gap-2"><input type={control.type} disabled={isDisabled} value={draft} min={control.min} max={control.max} maxLength={control.maxLength || 100} onChange={(event) => setDraft(event.target.value)} className="min-w-0 flex-1 rounded-xl bg-slate-950 px-3 py-3 text-base text-white outline-none ring-1 ring-white/10 focus:ring-fuchsia-300" placeholder={control.placeholder || ""} /><button disabled={isDisabled} className="rounded-xl bg-fuchsia-500 px-4 text-sm font-black text-white">Apply</button></div></form>;
  }
  return null;
}

export default function RemoteControllerPage() {
  const { pairToken } = useParams();
  const [status, setStatus] = React.useState("connecting");
  const [error, setError] = React.useState("");
  const [snapshot, setSnapshot] = React.useState(null);
  const [hostConnected, setHostConnected] = React.useState(true);
  const [feedback, setFeedback] = React.useState("");
  const socketRef = React.useRef(null);
  const reconnectTimerRef = React.useRef(null);
  const credentialsRef = React.useRef(null);
  const endedRef = React.useRef(false);
  const [sectionSelection, setSectionSelection] = React.useState({ contextId: "", index: 0 });

  const connect = React.useCallback((ticket) => {
    socketRef.current?.close();
    const socket = new WebSocket(remoteSocketUrl(ticket));
    socketRef.current = socket;
    setStatus("connecting");
    socket.onopen = () => { setStatus("connected"); setError(""); };
    socket.onmessage = (event) => {
      let message;
      try { message = JSON.parse(event.data); } catch { return; }
      if (message.type === "context.snapshot") {
        setSnapshot(message);
        setFeedback("");
      }
      if (message.type === "connection.ready") setHostConnected(Boolean(message.peerConnected));
      if (message.type === "presence" && message.role === "host") setHostConnected(Boolean(message.connected));
      if (message.type === "action.result") {
        setFeedback(message.ok ? "Done" : (message.detail || "Action failed"));
        if (message.ok && navigator.vibrate) navigator.vibrate(20);
      }
      if (message.type === "pong") setStatus("connected");
    };
    socket.onclose = (event) => {
      if (socketRef.current !== socket || endedRef.current) return;
      socketRef.current = null;
      if ([4410, 4411].includes(event.code)) {
        endedRef.current = true;
        window.localStorage.removeItem(CONTROLLER_STORAGE_KEY);
        setStatus("ended");
        setError(event.code === 4410 ? "This phone was disconnected by the main screen." : "The remote session ended.");
        return;
      }
      setStatus("reconnecting");
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = window.setTimeout(async () => {
        const credentials = credentialsRef.current;
        if (!credentials || endedRef.current) return;
        try {
          const result = await reconnectRemoteSession(credentials.sessionId, credentials.controllerReconnectToken);
          connect(result.ticket);
        } catch (reconnectError) {
          setError(reconnectError?.response?.data?.detail || "Could not reconnect.");
          setStatus("ended");
        }
      }, 2000);
    };
  }, []);

  React.useEffect(() => {
    endedRef.current = false;
    let cancelled = false;
    const bootstrap = async () => {
      try {
        let credentials;
        let ticket;
        if (pairToken) {
          try {
            const claimed = await claimOnce(pairToken);
            credentials = {
              sessionId: claimed.sessionId,
              controllerReconnectToken: claimed.controllerReconnectToken,
            };
            ticket = claimed.controllerTicket;
            window.localStorage.setItem(CONTROLLER_STORAGE_KEY, JSON.stringify(credentials));
            window.history.replaceState({}, "", "/remote/controller");
          } catch (claimError) {
            const saved = storedController();
            if (!saved) throw claimError;
            const result = await reconnectRemoteSession(saved.sessionId, saved.controllerReconnectToken);
            credentials = saved;
            ticket = result.ticket;
          }
        } else {
          credentials = storedController();
          if (!credentials) throw new Error("Scan a Pocket Play remote QR code to connect.");
          const result = await reconnectRemoteSession(credentials.sessionId, credentials.controllerReconnectToken);
          ticket = result.ticket;
        }
        if (cancelled) return;
        credentialsRef.current = credentials;
        connect(ticket);
      } catch (bootstrapError) {
        if (cancelled) return;
        setStatus("ended");
        setError(bootstrapError?.response?.data?.detail || bootstrapError.message || "Pairing failed.");
      }
    };
    bootstrap();
    return () => {
      cancelled = true;
      endedRef.current = true;
      window.clearTimeout(reconnectTimerRef.current);
      socketRef.current?.close();
    };
  }, [connect, pairToken]);

  const sendAction = React.useCallback((action, payload) => {
    if (socketRef.current?.readyState !== WebSocket.OPEN || !snapshot) return;
    setFeedback("Sending…");
    socketRef.current.send(JSON.stringify({
      v: 1,
      type: "action",
      messageId: newMessageId(),
      contextId: snapshot.contextId,
      action,
      payload,
    }));
  }, [snapshot]);

  const disabled = status !== "connected" || !hostConnected;

  const sections = snapshot?.sections || [];
  const pageName = String(snapshot?.page?.id || snapshot?.page?.title || "").toLowerCase();
  const preferredSectionIndex = Math.max(0, sections.findIndex((section) => String(section.title || "").toLowerCase().startsWith(pageName)));
  const activeSectionIndex = sectionSelection.contextId === snapshot?.contextId && sections[sectionSelection.index]
    ? sectionSelection.index
    : preferredSectionIndex;
  const activeSection = sections[activeSectionIndex] || sections[0];
  const focusedGame = snapshot?.state?.selectedGame || snapshot?.state?.focusedGame;
  const pageStatus = snapshot?.state?.totalPages > 1
    ? `Page ${snapshot.state.page || 1} of ${snapshot.state.totalPages}`
    : "";

  return (
    <main className="relative z-10 h-[100dvh] overflow-y-auto overscroll-contain bg-slate-950 px-4 pb-[max(2.5rem,env(safe-area-inset-bottom))] pt-5 text-white [-webkit-overflow-scrolling:touch]">
      <header className="sticky top-0 z-20 -mx-4 -mt-5 border-b border-white/10 bg-slate-950/90 px-4 py-4 backdrop-blur-xl">
        <div className="mx-auto flex max-w-xl items-center gap-3">
          <img src="/logo.png" alt="" className="h-11 w-11 rounded-xl" />
          <div className="min-w-0 flex-1"><p className="text-[10px] font-black uppercase tracking-[0.25em] text-fuchsia-300">Pocket Play remote</p><h1 className="truncate text-lg font-black">{snapshot?.page?.title || "Connecting…"}</h1></div>
          <span className={`h-3 w-3 rounded-full ${disabled ? "bg-amber-400" : "bg-emerald-400"}`} title={status} />
        </div>
        {sections.length > 0 && (
          <nav aria-label="Remote control sections" className="remote-section-tabs mx-auto mt-3 flex max-w-xl gap-2 overflow-x-auto pb-1">
            {sections.map((section, index) => (
              <button key={`${section.title}-${index}`} type="button" aria-label={`Go to ${section.title} controls`} aria-pressed={index === activeSectionIndex} onClick={() => setSectionSelection({ contextId: snapshot?.contextId || "", index })} className={`shrink-0 rounded-full border px-3 py-2 text-xs font-black transition ${index === activeSectionIndex ? "border-fuchsia-300/50 bg-fuchsia-500/25 text-white" : "border-white/10 bg-white/[0.05] text-indigo-200"}`}>{section.title}</button>
            ))}
          </nav>
        )}
      </header>

      <div className="mx-auto max-w-xl">
        {status === "ended" ? (
          <section className="mt-12 rounded-3xl border border-rose-300/20 bg-rose-400/10 p-7 text-center"><h2 className="text-2xl font-black">Remote unavailable</h2><p className="mt-3 text-rose-100/80">{error}</p><p className="mt-5 text-sm text-indigo-100/55">Show a new QR code on the main screen to pair again.</p></section>
        ) : !snapshot ? (
          <section className="py-20 text-center text-indigo-100/60"><div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-fuchsia-300/20 border-t-fuchsia-300" /><p className="mt-5">{status === "reconnecting" ? "Reconnecting to the screen…" : "Waiting for website controls…"}</p></section>
        ) : (
          <>
            {!hostConnected && <div className="mt-5 rounded-2xl border border-amber-300/20 bg-amber-300/10 p-4 text-center text-sm font-bold text-amber-100">Waiting for the main screen to reconnect…</div>}
            {(focusedGame || pageStatus) && (
              <div className="mt-5 flex items-center justify-between gap-3 rounded-2xl border border-cyan-300/15 bg-cyan-400/[0.07] px-4 py-3 text-left">
                <div className="min-w-0"><p className="text-[10px] font-black uppercase tracking-[0.2em] text-cyan-200/70">Current selection</p><p className="truncate text-sm font-black text-white">{focusedGame || snapshot.page.subtitle}</p></div>
                {pageStatus && <span className="shrink-0 rounded-full bg-white/[0.07] px-3 py-1 text-xs font-bold text-indigo-100">{pageStatus}</span>}
              </div>
            )}
            {activeSection && (
              <section className="mt-5 rounded-3xl border border-white/10 bg-indigo-950/60 p-4 shadow-xl shadow-black/20">
                <h2 className="mb-3 text-xs font-black uppercase tracking-[0.22em] text-fuchsia-200">{activeSection.title}</h2>
                <div className="grid grid-cols-2 items-stretch gap-3">
                  {(activeSection.controls || []).map((control) => <div key={control.id} className={control.type === "button" ? "h-full" : "col-span-2"}><Control control={control} sendAction={sendAction} disabled={disabled} /></div>)}
                </div>
              </section>
            )}
            {sections.length > 1 && <div className="mt-4 grid grid-cols-2 gap-3"><button type="button" disabled={activeSectionIndex <= 0} onClick={() => setSectionSelection({ contextId: snapshot?.contextId || "", index: Math.max(0, activeSectionIndex - 1) })} className="rounded-2xl border border-white/10 bg-white/[0.05] px-4 py-3 text-sm font-bold disabled:opacity-35">Previous section</button><button type="button" disabled={activeSectionIndex >= sections.length - 1} onClick={() => setSectionSelection({ contextId: snapshot?.contextId || "", index: Math.min(sections.length - 1, activeSectionIndex + 1) })} className="rounded-2xl border border-white/10 bg-white/[0.05] px-4 py-3 text-sm font-bold disabled:opacity-35">Next section</button></div>}
            {snapshot.page.subtitle && !focusedGame && <p className="mt-5 text-center text-sm text-indigo-100/55">{snapshot.page.subtitle}</p>}
            {feedback && <div className="fixed inset-x-4 bottom-4 z-30 mx-auto max-w-sm rounded-full bg-slate-800 px-5 py-3 text-center text-sm font-bold text-white shadow-2xl ring-1 ring-white/15">{feedback}</div>}
          </>
        )}
      </div>
    </main>
  );
}
